import TooltipHorizon from 'components/tooltip';
import { ReactElement, useEffect, useId, useRef, useState } from 'react';
import { twMerge } from 'tailwind-merge';

interface Option {
  label: string;
  value: string;
  className?: string;
}

interface CustomSelectProps {
  label?: string;
  extra?: string;
  variant?: string;
  state?: 'success' | 'error';
  disabled?: boolean;
  tooltip?: ReactElement;
  options: Option[];
  value?: string;
  onChange?: (value: string) => void;
}

const CustomSelect = ({
  label,
  extra,
  variant,
  state,
  disabled,
  tooltip,
  options,
  value,
  onChange,
}: CustomSelectProps) => {
  const id = useId();
  const [isOpen, setIsOpen] = useState(false);
  const [selected, setSelected] = useState<Option | undefined>(
    options.find(opt => opt.value === value) || options[0]
  );
  const wrapperRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        wrapperRef.current &&
        // eslint-disable-next-line
        !(wrapperRef.current as any).contains(event.target)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (option: Option) => {
    setSelected(option);
    setIsOpen(false);
    onChange?.(option.value);
  };

  return (
    <div className={extra} ref={wrapperRef}>
      {!!label && (
        <label
          htmlFor={id}
          className={`text-sm text-navy-700 dark:text-white ${
            variant === 'auth' ? 'ml-1.5 font-medium' : 'ml-3 font-bold'
          }`}
        >
          {label}
          {tooltip && (
            <TooltipHorizon
              extra=""
              placement="top"
              trigger={
                <span className="inline-flex justify-center items-center ml-2 min-w-4 w-4 h-4 border border-gray-500 rounded-full text-xs text-gray-500">
                  ?
                </span>
              }
              content={tooltip}
            />
          )}
        </label>
      )}

      <div
        className={twMerge(
          `relative flex h-10 w-full items-center rounded-xl border bg-gray-100 p-3 text-sm cursor-pointer ${
            disabled
              ? 'opacity-50 cursor-not-allowed'
              : state === 'error'
                ? 'border-red-500 text-red-500'
                : state === 'success'
                  ? 'border-green-500 text-green-500'
                  : 'border-gray-200 text-blue-700 dark:bg-gray-800 dark:text-white'
          }`
        )}
        onClick={() => !disabled && setIsOpen(prev => !prev)}
      >
        {selected?.label}
        <span className="ml-3 text-xs">▾</span>

        {isOpen && (
          <ul className="absolute -top-[185px] left-0 z-10 w-full bg-white dark:bg-[black] border rounded-xl shadow-lg overflow-hidden">
            {options.map(option => (
              <li
                key={option.value}
                onClick={e => {
                  e.stopPropagation();
                  handleSelect(option);
                }}
                className={twMerge(
                  `px-4 py-2 hover:bg-gray-100 dark:hover:bg-gray-600 cursor-pointer ${
                    option.value === selected?.value
                      ? 'bg-gray-200 dark:bg-gray-900'
                      : ''
                  }`,
                  option.className
                )}
              >
                {option.label}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

export default CustomSelect;
